// The base status indicator every dot/ring/square in the design composes:
// a tone (colour) and a shape (filled | hollow | square), because colour is
// never the only signal -- the design draws low confidence as the SAME amber
// as medium, with the ring SHAPE carrying the meaning, and a user-set value
// as an ink square with no colour meaning at all (§1.2, §2.1 conflict #4 of
// the 2026-09-06 UX execution plan).
export type StatusTone = 'ok' | 'warn' | 'error' | 'neutral';
export type StatusShape = 'filled' | 'hollow' | 'square';

const TONE_BG: Record<StatusTone, string> = {
  ok: 'bg-confidence-high',
  warn: 'bg-degraded',
  error: 'bg-money-negative',
  neutral: 'bg-money-neutral',
};

const TONE_BORDER: Record<StatusTone, string> = {
  ok: 'border-confidence-high',
  warn: 'border-degraded',
  error: 'border-money-negative',
  neutral: 'border-money-neutral',
};

export function StatusDot({
  tone,
  shape,
  label,
  showLabel = false,
  className,
}: {
  tone: StatusTone;
  shape: StatusShape;
  /** Always present for assistive tech; visually hidden unless showLabel. */
  label: string;
  showLabel?: boolean;
  className?: string;
}) {
  const shapeClasses =
    shape === 'square'
      ? 'h-2.5 w-2.5 rounded-[2px] bg-foreground'
      : shape === 'hollow'
        ? `h-2.5 w-2.5 rounded-full border-2 bg-transparent ${TONE_BORDER[tone]}`
        : `h-2.5 w-2.5 rounded-full ${TONE_BG[tone]}`;

  return (
    <span
      className={['inline-flex items-center gap-1.5', className].filter(Boolean).join(' ')}
      data-testid="status-dot"
      data-shape={shape}
      data-tone={tone}
    >
      <span aria-hidden="true" className={shapeClasses} />
      <span className={showLabel ? 'text-xs text-muted-foreground' : 'sr-only'}>{label}</span>
    </span>
  );
}

export default StatusDot;
